import { Alert, Box, SimpleGrid, Stack, useMatches } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import type { useTextTranslation } from "@hooks/translation/useTextTranslation";
import { SourcePanel } from "./SourcePanel";
import { TargetConfigPanel } from "./TargetConfigPanel";
import { TranslationResults } from "./TranslationResults";

interface Props {
  tx: ReturnType<typeof useTextTranslation>;
}

export const TextTranslationView: React.FC<Props> = ({ tx }) => {
  // The two panels sit side-by-side on ≥sm and stack on small screens. The
  // divider between them flips from a right border (side-by-side) to a bottom
  // border (stacked) so the connected-card look holds at every width.
  const dividerBorder = useMatches({
    base: { borderBottom: "1.5px solid var(--mantine-color-gray-4)" },
    sm: { borderRight: "1.5px solid var(--mantine-color-gray-4)" },
  });

  return (
    <Stack gap="md">
      {/* Single connected translation row: source | target (stacks on mobile) */}
      <SimpleGrid
        cols={{ base: 1, sm: 2 }}
        spacing={0}
        style={{
          border: "1.5px solid var(--mantine-color-gray-4)",
          borderRadius: "14px",
          background: "var(--mantine-color-white)",
          overflow: "hidden",
          boxShadow: "0 1px 2px rgba(20, 15, 10, 0.03)",
        }}
      >
        {/* Source panel */}
        <Box
          p="lg"
          style={{
            ...dividerBorder,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <SourcePanel
            sourceLang={tx.sourceLang}
            onSourceLangChange={tx.setSourceLang}
            text={tx.text}
            onTextChange={tx.setText}
            onClear={tx.reset}
            overLimit={tx.overLimit}
            disabled={tx.submitting}
          />
        </Box>

        {/* Target config */}
        <Box p="lg" style={{ display: "flex", flexDirection: "column" }}>
          <TargetConfigPanel
            targets={tx.targets}
            onTargetsChange={tx.setTargets}
            mode={tx.mode}
            onModeChange={tx.setMode}
            model={tx.model}
            onModelChange={tx.setModel}
            canSubmit={tx.canSubmit}
            submitting={tx.submitting}
            onSubmit={tx.submit}
          />
        </Box>
      </SimpleGrid>

      {tx.error && (
        <Alert color="red" icon={<IconAlertTriangle size={16} />}>
          {tx.error}
        </Alert>
      )}

      {tx.activeId && (
        <TranslationResults translationId={tx.activeId} targetLangs={tx.activeTargets} />
      )}
    </Stack>
  );
};
